/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import type { TaskModifierType } from '@kaiizen/kai-pro-api';
import { useId, useState } from 'react';

import { styleguideColors as COLORS, styleguideFontSize as FONT_SIZE, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';
import { DropdownIcon } from '@kaiizen/kai-shiki/react';

import { SettingsToggle } from '@/Client/Widgets/Settings';
import { MODIFIER_TYPE_COPY } from './modifierTypeCopy';

const CARD_STYLE = css`
  label: MODIFIER_CARD;
  display: flex;
  flex-direction: column;
  border: 1px solid ${COLORS.stroke};
  background: ${COLORS.white};
`;

/**
 * Mockup `.mod.is-off`. A disabled modifier drops to the paper ground and mutes its title and
 * body — but not its chip or its toggle, which an admin still has to read and reach.
 */
const CARD_OFF_STYLE = css`
  label: MODIFIER_CARD_OFF;
  background: ${COLORS.paper};
`;

const HEAD_STYLE = css`
  label: MODIFIER_CARD_HEAD;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 22px;
`;

/* Display face, as every 2026 heading is — this page had been reading in the body font, which is
   the single change that made it look unlike the rest of the redesign. */
const TITLE_STYLE = css`
  label: MODIFIER_CARD_TITLE;
  margin: 0 0 3px;
  font-family: ${FONT.display};
  font-size: ${pxToRem(FONT_SIZE.displaySmall)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: -0.01em;
`;

const TITLE_OFF_STYLE = css`
  label: MODIFIER_CARD_TITLE_OFF;
  color: ${COLORS.midtone};
  opacity: 0.65;
`;

/** The type descriptor rides with the title rather than under it, as in the mockup. */
const KIND_STYLE = css`
  label: MODIFIER_CARD_KIND;
  font-family: ${FONT.body};
  font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: 0;
  color: ${COLORS.ink};
`;

const KIND_OFF_STYLE = css`
  label: MODIFIER_CARD_KIND_OFF;
  color: ${COLORS.midtone};
`;

const META_STYLE = css`
  label: MODIFIER_CARD_META;
  margin: 2px 0 0;
  font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  color: ${COLORS.midtone};
`;

const CONTROLS_STYLE = css`
  label: MODIFIER_CARD_CONTROLS;
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 14px;
`;

/** Mockup `.logic-chip`: always the Kai type, never the label. */
const CHIP_STYLE = css`
  label: MODIFIER_CARD_CHIP;
  padding: 5px 11px 4px;
  border: 1px solid ${COLORS.stroke};
  font-family: ${FONT.mono};
  font-size: ${pxToRem(FONT_SIZE.monoCaption)};
  font-weight: ${FONT_WEIGHT.bold};
  /* Between the mono tokens' 0.08em and 0.12em; the chip sits tighter than a caption. */
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${COLORS.midtone};
  white-space: nowrap;
`;

const CHEVRON_STYLE = css`
  label: MODIFIER_CARD_CHEVRON;
  display: flex;
  padding: 4px;
  border: 0;
  background: none;
  cursor: pointer;
  color: ${COLORS.midtone};
  transition: transform 0.16s;

  svg {
    width: 16px;
    height: 16px;
  }
`;

/** Collapsed points down, expanded points up — one icon, rotated, as the mockup does it. */
const CHEVRON_EXPANDED_STYLE = css`
  label: MODIFIER_CARD_CHEVRON_EXPANDED;
  transform: rotate(180deg);
`;

const ERROR_STYLE = css`
  label: MODIFIER_SAVE_ERROR;
  margin: 0;
  padding: 0 22px 14px;
  font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  color: ${COLORS.error};
`;

const BODY_STYLE = css`
  label: MODIFIER_CARD_BODY;
  padding: 0 22px 22px;
`;

const BODY_OFF_STYLE = css`
  label: MODIFIER_CARD_BODY_OFF;
  opacity: 0.55;
`;

/**
 * One modifier in Org Settings → Modifiers.
 *
 * Collapsed on load; the chevron expands this card alone. The title shows the org's label and
 * falls back to the Kai type name, while the chip always names the type — so a rename never
 * disguises which financial rules a modifier follows.
 */
export const ModifierCard = ({
  modifierKey,
  type,
  label,
  isEnabled,
  onToggleEnabled,
  errorMessage,
  children,
}: {
  modifierKey: string;
  type: TaskModifierType;
  label?: string;
  isEnabled: boolean;
  onToggleEnabled?: (enabled: boolean) => void;
  /** Why the last save for this modifier failed, if it did. */
  errorMessage?: string;
  children?: React.ReactNode;
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const bodyId = useId();
  const copy = MODIFIER_TYPE_COPY[type];
  const title = label?.trim() || copy.name;

  return (
    <section css={[CARD_STYLE, !isEnabled && CARD_OFF_STYLE]} data-testid={`modifier-card-${modifierKey}`}>
      <div css={HEAD_STYLE}>
        <div>
          <h3 css={[TITLE_STYLE, !isEnabled && TITLE_OFF_STYLE]}>
            {title} <span css={[KIND_STYLE, !isEnabled && KIND_OFF_STYLE]}>{`(${copy.kind})`}</span>
          </h3>
          {/* Display has no rule summary: it has no financial rules to summarize. */}
          {copy.meta ? <p css={META_STYLE}>{copy.meta}</p> : null}
        </div>

        <span css={CONTROLS_STYLE}>
          <span css={CHIP_STYLE}>{`${copy.name} Logic`}</span>
          <SettingsToggle
            checked={isEnabled}
            onClick={() => onToggleEnabled?.(!isEnabled)}
            aria-label={`Enable ${title}`}
          />
          <button
            aria-controls={bodyId}
            aria-expanded={!isCollapsed}
            aria-label={isCollapsed ? `Expand ${title}` : `Collapse ${title}`}
            css={[CHEVRON_STYLE, !isCollapsed && CHEVRON_EXPANDED_STYLE]}
            onClick={() => setIsCollapsed(collapsed => !collapsed)}
            type='button'
          >
            <DropdownIcon />
          </button>
        </span>
      </div>

      {
        /* Outside the collapsible body on purpose: the enable toggle sits in the head, so a
          rejected toggle has to report itself on a collapsed card too. */
      }
      {errorMessage ? <p css={ERROR_STYLE} role='alert'>{errorMessage}</p> : null}

      {isCollapsed ? null : <div css={[BODY_STYLE, !isEnabled && BODY_OFF_STYLE]} id={bodyId}>{children}</div>}
    </section>
  );
};
